import { For, Setter, Show, Signal, createContext, createEffect, createMemo, createSignal, useContext } from "solid-js";
import STInput from "../st-input/st-input";
import { FilterGroupInstanceDragHandler, FilterGroupDropFilterHandler, FilterGroupDropGroupHandler } from './FilterGroupDropHandler';
import { FilterDragHandler } from './FilterDragHandler';
import { FilterDropHandler } from './FilterDropHandler';
import { setUserFilters, userFilters } from "./UserFilterStore";
import { UserFilterGroupInstance } from "./UserFilterGroupInstance";
import STButton, { ButtonType } from "../st-button/st-button";
import { viewFilters as api } from 'st-app/api';
import './st-view-filters.css';
import { FilterInfoInstance, SharedFilterInstance } from "./FilterInfoInstance";
import STToolButton from "../st-tool-button/st-tool-button";
import { showConfirmation } from "../st-confirmation-dialog/st-confirmation-dialog";

const SHARE_FILTER_VIEW_GU = 'ED708DB0-0944-4971-89F1-BB040A797159';

const FilterEditorContext = createContext({ primaryObjectGU: '' });

export function FilterEditor(props: { primaryObjectGU: string, setShowDialog: Setter<boolean> }) {
    const checklists = createMemo(() => userFilters.allChecklists);
    const sharedFilters = createMemo(() => userFilters.sharedFilters);

    const [editingGroup, setEditingGroup] = createSignal<UserFilterGroupInstance>();
    const [selectedGroup, setSelectedGroup] = createSignal<UserFilterGroupInstance>();

    const [activeFilter, setActiveFilter] = createSignal<FilterInfoInstance>();

    async function addFolder() {
        const group = new UserFilterGroupInstance(await api.CreateNewFolder());

        setUserFilters('allGroups', [group, ...userFilters.allGroups]);
        setEditingGroup(group);
    }

    return <FilterEditorContext.Provider value={{ primaryObjectGU: props.primaryObjectGU }}>
         <div class="flexbox vertical auto">
            <div class="flexbox horizontal auto">
                <div class="flexbox vertical auto">
                    <div class="flexbox vertical auto" style="max-height: 350px; overflow-y: auto; overflow-x: hidden">
                        <GroupTree editingGroup={[editingGroup, setEditingGroup]} selectedGroup={[selectedGroup, setSelectedGroup]} />
                    </div>
                    <STButton type="default" icon="fa fa-plus" onClick={addFolder}>
                        Add Folder
                    </STButton>
                </div>
                <div class="auto">
                    <Show when={sharedFilters().length > 0}>
                        <div>
                            <h4>Shared Filters</h4>
                            <ul class="filter-tabs flexbox vertical" role="tablist" style="padding-left: 25px; margin-bottom: 15px;">
                                <For each={sharedFilters()}>{(filter) =>
                                    <SharedFilter filter={filter} selectedGroup={[selectedGroup, setSelectedGroup]} />
                                }</For>
                            </ul>
                        </div>
                    </Show>
                    <Show when={selectedGroup()}>
                        <GroupFilters group={selectedGroup()!} activeFilter={[activeFilter, setActiveFilter]} setShowDialog={props.setShowDialog} />
                    </Show>
                </div>
            </div>
        </div>
    </FilterEditorContext.Provider>
}

function GroupFilters(props: { group: UserFilterGroupInstance, activeFilter: Signal<FilterInfoInstance | undefined>, setShowDialog: Setter<boolean> }) {
    const ctx = useContext(FilterEditorContext);
    const filters = createMemo(() => {
        const filterGroupGU = props.group.filterGroupGU;

        return userFilters.allFilters
            .filter(f => f.primaryGU === ctx.primaryObjectGU)
            .filter(f => f.filterGroupGU === filterGroupGU)
            .sort((a, b) => a.order - b.order);
    });

    const [editingFilter, setEditingFilter] = createSignal<FilterInfoInstance>();
    const [shareFilter, setShareFilter] = createSignal<FilterInfoInstance>();

    const [showShareModal, setShowShareModal] = createSignal(false);
    createEffect(() => setShowShareModal(!!shareFilter()));
    createEffect(() => {
        const show = showShareModal();
        const filter = shareFilter();

        if (!show && !!filter) {
            setShareFilter(undefined);
        }
    });

    function openShareView(filterGU: string) {
        if (window.ST.OpenViewInDialog) {
            props.setShowDialog(false);
            window.ST.OpenViewInDialog(SHARE_FILTER_VIEW_GU, filterGU)
                .always(($view: any) => {
                    $view.closest('.modal').on('hidden.bs.modal', () => props.setShowDialog(true));
                });
        }
    }

    return <ul class="filter-tabs flexbox vertical" role="tablist" style="max-height: 350px; overflow-y: auto; overflow-x: hidden">
        <For each={filters()}>{(filter) =>
            <li class="flexbox horizontal middle padded"
                classList={{ 'drop-target': filter.isDropTarget[0]() }}
                use:draggable={new FilterDragHandler(props.group, filter)}
                use:droppable={new FilterDropHandler(props.group, filter)}
                role="tab">

                <div class="auto">
                    <Show when={filter === editingFilter()}>
                        <STInput placeholder="Filter Name" value={filter.name$}
                            selected={true} focused={true}
                            onEnterKey={() =>  setEditingFilter(undefined)}
                            onBlur={() => setEditingFilter(undefined)}
                        />
                    </Show>
                    <Show when={filter !== editingFilter()}>
                        {filter.name$[0]()}
                    </Show>
                </div>
                <div class="tools flexbox horizontal middle">
                    <STToolButton label="Edit" icon="fa fa-edit" onClick={() => requestAnimationFrame(() => setEditingFilter(filter))} />
                    <STToolButton label="Share" icon="fa fa-share" onClick={() => openShareView(filter.filterGU!)} />
                    <STToolButton label="Delete" icon="fa fa-remove" onClick={() => {
                        showConfirmation('Delete Filter', 'This operation cannot be undone. Do you want to delete this filter?')
                            .then(() => {
                                userFilters.removeFilter(filter)
                            });
                    } } />
                </div>
            </li>
        }</For>
    </ul>
}

function SharedFilter(props: { filter: SharedFilterInstance, selectedGroup: Signal<UserFilterGroupInstance | undefined> }) {
    const filter = props.filter;
    const group = userFilters.allGroups.find(g => filter.filterGroupGU === g.filterGroupGU);

    return <li class="flexbox horizontal middle padded"
        classList={{ 'drop-target': filter.isDropTarget[0]() }}
        use:draggable={new FilterDragHandler(group!, filter)}
        use:droppable={new FilterDropHandler(group!, filter)}
        role="tab">

        <div class="auto">
            {filter.name$[0]()} ({filter.fromUser})
        </div>
        <div class="tools flexbox horizontal middle">
            <STToolButton label="Add" icon="fa fa-add" onClick={() => userFilters.acceptSharedFilter(props.filter, props.selectedGroup[0]()?.filterGroupGU)} />
            <STToolButton label="Delete" icon="fa fa-remove" onClick={() => userFilters.removeSharedFilter(props.filter)} />
        </div>
    </li>

}

class GroupTreeProps {
    editingGroup!: Signal<UserFilterGroupInstance | undefined>;
    selectedGroup!: Signal<UserFilterGroupInstance | undefined>
}
const GroupTreeContext = createContext(new GroupTreeProps());

function GroupTree(props: GroupTreeProps) {
    const rootGroups = createMemo(() => userFilters.allGroups.filter(g => !g.parentGroupGU).sort((a, b) => a.order - b.order));

    createEffect(() => {
        if (!props.selectedGroup[0]()) {
            props.selectedGroup[1](rootGroups()[0]);
        }
    });

    return <GroupTreeContext.Provider value={props}>
        <For each={rootGroups()}>{(group) =>
            <GroupNode group={group} />
        }</For>
        <GroupNode group={new UserFilterGroupInstance({ filterGroupGU: '', groupName: 'Other', order: 0 })} />
    </GroupTreeContext.Provider>
}

function GroupNode(props: { group: UserFilterGroupInstance }) {
    const group = props.group;
    const ctx = useContext(GroupTreeContext);

    const childGroups = createMemo(() => userFilters.allGroups
        .filter(g => group.filterGroupGU && g.parentGroupGU === group.filterGroupGU)
        .sort((a, b) => a.order - b.order)
    );
    const [editingGroup, setEditingGroup] = ctx.editingGroup;
    const [selectedGroup, setSelectedGroup] = ctx.selectedGroup;

    const isExpanded = createMemo(() => userFilters.isGroupExpanded(props.group?.filterGroupGU || ''));
    const filters = createMemo(() => {
        const editorCtx = useContext(FilterEditorContext);

        return userFilters.allFilters
            .filter(f => f.primaryGU === editorCtx.primaryObjectGU)
            .filter(f => f.filterGroupGU === props.group.filterGroupGU);
    });

    function deleteGroup(group: UserFilterGroupInstance) {
        showConfirmation('Delete Group', 'This operation cannot be undone. Do you want to delete this group?')
            .then(() => {
                api.DeleteGroup(group.filterGroupGU!);

                const groups = userFilters.allGroups
                    .filter(g => g.filterGroupGU !== group.filterGroupGU)
                    .map(g => new UserFilterGroupInstance(g));

                const filters = userFilters.allFilters.map(f => new FilterInfoInstance(f));
                for (const filter of filters) {
                    if (filter.filterGroupGU === group.filterGroupGU) {
                        filter.filterGroupGU = '';
                    }
                }

                setUserFilters('allFilters', filters);
                setUserFilters('allGroups', groups);
            });
    }

    return <div
        style="z-index: 2; padding-left: 20px;"
        classList={{ 'drop-target': group.isDropTarget[0](), 'dragging': group.isDragging[0]() }}
        use:droppable={new FilterGroupDropFilterHandler(group)}
        // @ts-ignore solid-js allows multiple using statements
        use:droppable={new FilterGroupDropGroupHandler(group)}
        use:draggable={!!group.filterGroupGU ? new FilterGroupInstanceDragHandler(group) : undefined}>

        <Show when={group.name[0]() && editingGroup() !== group}>
            <div class="flexbox horizontal middle padded folder"
                aria-expanded={isExpanded()}
                classList={{ 'active': group.filterGroupGU === selectedGroup()?.filterGroupGU }}>
                <Show when={childGroups().length > 0}>
                    <span class={`fa ${isExpanded() ? 'fa-caret-down' : 'fa-caret-right'}`}
                        role="button" 
                        onClick={() => userFilters.toggleExpandedGroup(group.filterGroupGU || '')}
                        aria-label="Toggle Group"
                    ></span>
                </Show>
                <span class="far fa-folder"></span>
                <h4 class="auto" role="button" onClick={() => setSelectedGroup(group)}>{group.name[0]()} ({filters().length})</h4>
                <Show when={group.filterGroupGU}>
                    <div class="tools">
                        <STToolButton icon="fa fa-edit s-link" label="Edit" onClick={() => setEditingGroup(group)} />
                        <STToolButton icon="fa fa-times s-link" label="Delete" onClick={() => deleteGroup(group)} />
                    </div>
                </Show>
            </div>
        </Show>
        <Show when={editingGroup() === group}>
            <div style="padding: 5px;">
                <STInput value={group.name}
                    selected={true} focused={true}
                    onEnterKey={() => {
                        api.UpdateGroup(group);
                        setEditingGroup(undefined);
                    }}
                    onBlur={() => {
                        api.UpdateGroup(group);
                        setEditingGroup(undefined);
                    }}
                    style="font-weight: bold;" />
            </div>
        </Show>
        <For each={childGroups()}>{(group) =>
            <GroupNode group={group} />
        }</For>
    </div>
}
